<template>
  <LcLotteryEditorCard
    ref="card"
    v-bind="{ complete, remaining, removable, warning }"
    v-model:expanded="isExpanded"
    :[EDITOR_PLAY_ID_ATTRIBUTE]="play.id"
    :clear-disabled="!clearEnabled"
    :clear-label="$t('numberlottery.playslip.button.clear')"
    :done-label="$t('powerballxsandos.remainingnumbers.done')"
    :quick-pick-label="$t('numberlottery.playslip.button.quickpick')"
    radius="medium"
    :remove-aria-label="$t('editor.card.remove.arialabel')"
    @clear="handleClear"
    @collapsed="notifyCollapsed(play.id)"
    @expanded="scrollExpandedIntoView(cardRef)"
    @quick-pick="handleQuickpick"
    @remove="store.discardPlay(play.id)"
    @update:expanded="handleExpandedChange"
  >
    <template #headline="{ countClass }">
      <i18n-t keypath="powerballxsandos.remainingnumbers" :plural="visualRemaining" scope="global">
        <template #remaining>
          <span :class="countClass">{{ visualRemaining }}</span>
        </template>
      </i18n-t>
    </template>

    <template #summary>
      <ul
        ref="summary"
        class="number-lottery-xs-and-os-picker__summary flex-1 gap-050"
        :style="{ '--summary-columns': summaryColumns }"
      >
        <li v-for="{ key, rolling, team } in summaryTeams" :key="key">
          <LcLotteryTeamTile
            v-if="team"
            v-bind="{ licensedTerritory, logoVariant, lotteryId, team }"
            :class="{ 'number-lottery-xs-and-os-picker__tile--rolling': rolling }"
            selected
            size="medium-large"
          />
          <span
            v-else
            class="number-lottery-xs-and-os-picker__placeholder block bg-secondary-secondary-disabled rounded-s"
          />
        </li>
      </ul>
    </template>

    <ul
      :class="[
        'number-lottery-xs-and-os-picker__fields border-100 rounded-m bg-primary-primary px-100 py-200',
        { 'number-lottery-xs-and-os-picker__fields--locked': isAnimating(play.id) },
      ]"
    >
      <li
        v-for="({ name, selectableMax, selectableNumbers }, index) in config.numbers"
        :key="name"
        ref="fieldItems"
      >
        <EditorNumberLotteryXsAndOsPickerField
          v-bind="{ lotteryId, name, play, selectableMax, selectableNumbers }"
          :separated="index > BASE_FIELD_INDEX"
          @complete="scrollToNextIncompleteField(config, play)"
        />
      </li>
    </ul>
  </LcLotteryEditorCard>
</template>

<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue'

import { decodeXsAndOsTeamName, LcLotteryTeamTile } from '@lottocom/frontend-lotteries'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'
import type { EditorPlay } from '../stores/numberLottery'

import { EDITOR_PLAY_ID_ATTRIBUTE } from '../constants/editor'
import { EDITOR_QUICK_PICK_ROLL_TIMING } from '../constants/quickPickAnimation'
import { useNumberLotteryStore } from '../stores/numberLottery'
import { buildCardFields } from '../utils/editorFields'

type Props = {
  config: Config
  lotteryId: LotteryId
  play: EditorPlay
}

const BASE_FIELD_INDEX = 0
const SUMMARY_TILE_GAP = 4
const SUMMARY_TILE_HEIGHT = 62
const SUMMARY_TILE_WIDTH = 50
const SUMMARY_UNMEASURED_ROWS = 2

const { config, lotteryId, play } = defineProps<Props>()

const { t } = useI18n()
const store = useNumberLotteryStore(lotteryId)

const licensedTerritory = useLicensedTerritory({ raw: true })
const logoVariant = useXsAndOsLogoVariant()
const { cancel, getNumberState, getSummaryPlay, isAnimating, notifyCollapsed, triggerSinglePlay } =
  useEditorQuickPickAnimation()

const { scrollExpandedIntoView, scrollExpandingPlayIntoView } = useExpandableScroll()
const cardRef = useTemplateRef<ComponentPublicInstance>('card')
const fieldRefs = useTemplateRef<HTMLElement[]>('fieldItems')
const summaryRef = useTemplateRef<HTMLElement>('summary')
const { width: summaryWidth } = useElementSize(summaryRef)
const { scrollToNextIncompleteField } = useNumberLotteryFieldScroll(fieldRefs, cardRef)

const clearEnabled = computed(() => !store.isPlayEmpty(play))

const complete = computed(() => store.isFieldComplete(config, play, BASE_FIELD_INDEX))

const isExpanded = computed({
  get: () => store.expandedPlayId === play.id,
  set: (expanded) => store.setExpandedPlay(expanded ? play.id : null),
})

const warning = computed(() =>
  !isExpanded.value && store.isPlayFlagged(config, play)
    ? t('editor.play.badge.incomplete')
    : undefined,
)

const remaining = computed(() =>
  store.remainingInField(play, BASE_FIELD_INDEX, config.numbers[BASE_FIELD_INDEX].selectableMax),
)

const removable = computed(
  () => isExpanded.value && store.isPlayEmpty(play) && store.plays.length > 1,
)

const summaryTeams = computed(() =>
  buildCardFields(config, getSummaryPlay(play), () => ({})).flatMap(({ name, numbers }) =>
    numbers.map(({ value }, index) => ({
      key: `${name}-${index}`,
      rolling: getNumberState(play.id, value, name) === 'rolling',
      team: value === undefined ? null : decodeXsAndOsTeamName(value),
    })),
  ),
)

const summaryColumns = computed(() => {
  const count = summaryTeams.value.length
  if (!count) return 1

  const perRow = Math.floor(
    (summaryWidth.value + SUMMARY_TILE_GAP) / (SUMMARY_TILE_WIDTH + SUMMARY_TILE_GAP),
  )
  const rows = perRow > 0 ? Math.ceil(count / perRow) : SUMMARY_UNMEASURED_ROWS

  return Math.ceil(count / rows)
})

const visualRemaining = useEditorVisualRemaining(() => remaining.value)

const handleClear = () => {
  if (!clearEnabled.value || isAnimating(play.id)) return
  store.clearPlay(play.id)
}

const handleExpandedChange = (expanded: boolean) => {
  cancel(play.id)
  if (!expanded) return
  scrollExpandingPlayIntoView(cardRef.value)
}

const handleQuickpick = () => {
  triggerSinglePlay({
    config,
    ignoredFields: store.getIgnoredFieldNames(config, play),
    kind: 'grid',
    play,
    quickPick: () => store.quickPick(play.id, config),
    replaceAll: store.isPlayComplete(config, play),
  })
}
</script>

<style lang="scss" scoped>
$summary-tile-height: v-bind("SUMMARY_TILE_HEIGHT + 'px'");
$summary-tile-width: v-bind("SUMMARY_TILE_WIDTH + 'px'");
$roll-tick: v-bind("EDITOR_QUICK_PICK_ROLL_TIMING.tick + 'ms'");

.number-lottery-xs-and-os-picker {
  &__fields {
    border-style: solid;
    border-color: token('color/border/secondary/secondary');

    &--locked {
      pointer-events: none;
    }
  }

  &__summary {
    display: grid;
    grid-template-columns: repeat(var(--summary-columns), $summary-tile-width);
    min-width: 0;
  }

  &__placeholder {
    width: $summary-tile-width;
    height: $summary-tile-height;
  }

  &__tile--rolling {
    overflow: hidden;

    :deep(> *) {
      animation: number-lottery-xs-and-os-tile-roll $roll-tick linear infinite;
    }
  }
}

@keyframes number-lottery-xs-and-os-tile-roll {
  from {
    opacity: 0.35;
    transform: translateY(-55%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
